const SELECTOR = 'amz-advanced-mega-drawer';
const DESKTOP_QUERY = '(min-width: 990px)';
const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

class AmzAdvancedMegaDrawer extends HTMLElement {
  #activeTrigger = null;
  #closeTimer = 0;
  #openTimer = 0;
  #hideTimer = 0;
  #restoreFocusTo = null;
  #triggerCleanups = [];
  #openIntent = null;
  #openedAt = 0;
  #pointerX = -1;
  #pointerY = -1;
  #scrollCleanups = [];
  #scrollStops = [];

  connectedCallback() {
    this.mode = this.dataset.mode || 'desktop';
    this.openMode = this.dataset.openMode || 'both';
    const ownerSelector = this.id ? `[data-amz-owner="${CSS.escape(this.id)}"]` : '';
    this.layer = this.querySelector('[data-amz-layer]') || (ownerSelector ? document.querySelector(ownerSelector) : null);
    this.dialog = this.layer?.querySelector('[data-amz-dialog]') || this.querySelector('[data-amz-dialog]');
    this.mobileTrigger = this.querySelector('[data-amz-mobile-trigger]');
    this.closeButtons = this.layer ? [...this.layer.querySelectorAll('[data-amz-close]')] : [];
    this.tabs = this.layer ? [...this.layer.querySelectorAll('[data-amz-tab]')] : [];
    this.panels = this.layer ? [...this.layer.querySelectorAll('[data-amz-panel]')] : [];
    this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
    this.finePointerMedia = window.matchMedia(FINE_POINTER_QUERY);

    if (!this.layer || !this.dialog) return;

    this.#mountLayer();
    this.mobileTrigger?.addEventListener('click', this.#onMobileTrigger);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.#onCloseClick));
    this.tabs.forEach((tab) => tab.addEventListener('click', this.#onTabClick));
    this.dialog.addEventListener('pointerenter', this.#clearCloseTimer);
    this.dialog.addEventListener('pointerleave', this.#onDialogPointerLeave);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('pointermove', this.#onDocumentPointerMove, { passive: true });
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
    this.desktopMedia.addEventListener('change', this.#onBreakpointChange);

    this.#bindDesktopTriggers();
    this.#activatePanel('one');
    this.#setupMobileKineticScroll();
    this.setAttribute('data-amz-ready', '');
  }

  disconnectedCallback() {
    this.mobileTrigger?.removeEventListener('click', this.#onMobileTrigger);
    this.closeButtons?.forEach((button) => button.removeEventListener('click', this.#onCloseClick));
    this.tabs?.forEach((tab) => tab.removeEventListener('click', this.#onTabClick));
    this.dialog?.removeEventListener('pointerenter', this.#clearCloseTimer);
    this.dialog?.removeEventListener('pointerleave', this.#onDialogPointerLeave);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointermove', this.#onDocumentPointerMove);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    this.desktopMedia?.removeEventListener('change', this.#onBreakpointChange);
    this.#unbindDesktopTriggers();
    this.#teardownMobileKineticScroll();
    this.#clearTimers();
    this.#releaseScrollLock();
    this.removeAttribute('open');
    this.removeAttribute('data-amz-ready');
    this.layer?.classList.remove('is-open');
    this.layer?.setAttribute('aria-hidden', 'true');
    this.mobileTrigger?.setAttribute('aria-expanded', 'false');

    // Horizon temporarily disconnects and reconnects header children during hydration.
    // Move the portal back into its host so the next connectedCallback can remount it.
    if (this.layer && this.layer.parentNode !== this) this.appendChild(this.layer);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  get isCorrectViewport() {
    return this.mode === 'desktop' ? this.desktopMedia.matches : !this.desktopMedia.matches;
  }

  #mountLayer() {
    this.layer.classList.add(`amz-mega__layer--${this.mode}`);
    if (this.id) this.layer.dataset.amzOwner = this.id;
    this.layer.style.cssText = this.style.cssText;
    this.layer.setAttribute('aria-hidden', 'true');
    this.layer.hidden = false;
    document.body.appendChild(this.layer);
  }

  #onSectionLoad = () => {
    this.#bindDesktopTriggers();
  };

  #onBreakpointChange = () => {
    this.close({ restoreFocus: false, immediate: true });
    this.#bindDesktopTriggers();
  };

  #bindDesktopTriggers() {
    this.#unbindDesktopTriggers();
    if (this.mode !== 'desktop') return;

    const host = this.closest('header-menu') || this.parentElement;
    const triggers = host ? [...host.querySelectorAll('[data-amz-drawer-trigger]')] : [];

    triggers.forEach((trigger) => {
      const onClick = (event) => {
        if (!this.desktopMedia.matches || !this.#allowsClick()) return;
        event.preventDefault();
        const key = trigger.dataset.amzDrawerTrigger;
        if (this.isOpen && this.#activeTrigger === trigger) {
          this.close({ restoreFocus: false });
        } else {
          this.open(key, trigger, true, 'click');
        }
      };
      const onPointerEnter = (event) => {
        if (!this.#allowsDesktopHover(event)) return;
        this.#recordPointer(event);
        this.#clearTimers();
        if (this.isOpen && this.#activeTrigger === trigger) return;
        this.#openTimer = window.setTimeout(() => {
          this.open(trigger.dataset.amzDrawerTrigger, trigger, false, 'hover');
        }, 120);
      };
      const onPointerLeave = (event) => {
        if (!this.#allowsDesktopHover(event) || this.#openIntent !== 'hover') return;
        this.#recordPointer(event);
        this.#scheduleClose();
      };
      const onFocus = () => {
        if (!this.desktopMedia.matches || !trigger.matches(':focus-visible')) return;
        if (this.openMode === 'hover' || this.openMode === 'both') {
          this.open(trigger.dataset.amzDrawerTrigger, trigger, false, 'keyboard');
        }
      };

      trigger.addEventListener('click', onClick);
      trigger.addEventListener('pointerenter', onPointerEnter);
      trigger.addEventListener('pointerleave', onPointerLeave);
      trigger.addEventListener('focus', onFocus);
      this.#triggerCleanups.push(() => {
        trigger.removeEventListener('click', onClick);
        trigger.removeEventListener('pointerenter', onPointerEnter);
        trigger.removeEventListener('pointerleave', onPointerLeave);
        trigger.removeEventListener('focus', onFocus);
      });
    });
  }

  #unbindDesktopTriggers() {
    this.#triggerCleanups.forEach((cleanup) => cleanup());
    this.#triggerCleanups = [];
  }

  #allowsClick() {
    return this.openMode === 'click' || this.openMode === 'both';
  }

  #allowsDesktopHover(event) {
    return (
      this.mode === 'desktop' &&
      this.desktopMedia.matches &&
      this.finePointerMedia.matches &&
      event.pointerType !== 'touch' &&
      (this.openMode === 'hover' || this.openMode === 'both')
    );
  }

  #onMobileTrigger = (event) => {
    if (this.mode !== 'mobile' || this.desktopMedia.matches) return;
    event.preventDefault();
    this.open('one', this.mobileTrigger, true, 'mobile');
  };

  #onCloseClick = (event) => {
    event.preventDefault();
    this.close();
  };

  #onTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    this.#activatePanel(tab.dataset.amzTab || 'one');
  };

  #onDialogPointerLeave = (event) => {
    if (!this.#allowsDesktopHover(event) || this.#openIntent !== 'hover') return;
    this.#recordPointer(event);
    this.#scheduleClose();
  };

  #onDocumentPointerMove = (event) => {
    if (this.mode !== 'desktop' || !this.isOpen || this.#openIntent !== 'hover') return;
    if (!this.desktopMedia.matches || !this.finePointerMedia.matches || event.pointerType === 'touch') return;

    this.#recordPointer(event);
    if (this.#pointerInside(this.#activeTrigger) || this.#pointerInside(this.dialog)) {
      this.#clearCloseTimer();
    } else if (!this.#closeTimer) {
      this.#scheduleClose();
    }
  };

  #onKeyDown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') this.#trapFocus(event);

    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && this.tabs.includes(document.activeElement)) {
      event.preventDefault();
      this.#activatePanel(event.key === 'ArrowRight' ? 'two' : 'one', true);
    }
  };

  open(key = 'one', trigger = null, moveFocus = false, intent = 'programmatic') {
    if (!this.layer || !this.dialog || !this.isCorrectViewport) return;

    document.querySelectorAll(`${SELECTOR}[open]`).forEach((drawer) => {
      if (drawer !== this && typeof drawer.close === 'function') drawer.close({ restoreFocus: false, immediate: true });
    });

    this.#clearTimers();
    this.#activatePanel(key === 'two' ? 'two' : 'one');
    this.#activeTrigger = trigger instanceof HTMLElement ? trigger : null;
    this.#openIntent = intent;
    this.#openedAt = window.performance.now();
    this.#restoreFocusTo = this.#activeTrigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    this.setAttribute('open', '');
    this.layer.setAttribute('aria-hidden', 'false');
    this.#setTriggerExpanded(true);
    document.documentElement.classList.add('amz-mega-scroll-lock');

    // Force a closed-state paint before starting the slide/fade transition.
    this.layer.classList.remove('is-open');
    void this.layer.offsetWidth;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.layer?.classList.add('is-open'));
    });

    if (moveFocus) {
      window.setTimeout(() => {
        const focusTarget = this.mode === 'mobile' ? this.layer?.querySelector('.amz-mega__floating-close') : this.dialog;
        if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
      }, 80);
    }
  }

  close(options = {}) {
    const { restoreFocus = true, immediate = false } = options;
    if (!this.isOpen && !this.layer?.classList.contains('is-open')) return;

    this.#clearTimers();
    this.#stopMobileKineticScroll();
    this.removeAttribute('open');
    this.layer?.classList.remove('is-open');
    this.#setTriggerExpanded(false);
    this.#releaseScrollLock();

    if (this.layer) {
      if (immediate) {
        this.layer.setAttribute('aria-hidden', 'true');
      } else {
        this.#hideTimer = window.setTimeout(
          () => this.layer?.setAttribute('aria-hidden', 'true'),
          this.#getExitDuration()
        );
      }
    }

    if (restoreFocus && this.#restoreFocusTo?.isConnected) {
      this.#restoreFocusTo.focus({ preventScroll: true });
    }
    this.#activeTrigger = null;
    this.#restoreFocusTo = null;
    this.#openIntent = null;
    this.#openedAt = 0;
  }

  #activatePanel(key, moveFocus = false) {
    this.#stopMobileKineticScroll();
    this.tabs?.forEach((tab) => {
      const active = tab.dataset.amzTab === key;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active && moveFocus) tab.focus();
    });
    this.panels?.forEach((panel) => {
      const active = panel.dataset.amzPanel === key;
      const wasActive = !panel.hidden;
      panel.hidden = !active;

      if (active && this.isOpen && !wasActive) {
        panel.classList.add('amz-mega__panel--entering');
        void panel.offsetWidth;
        window.requestAnimationFrame(() => panel.classList.remove('amz-mega__panel--entering'));
      }
    });
  }

  #scheduleClose() {
    if (this.#openIntent !== 'hover') return;
    this.#clearCloseTimer();
    const elapsed = Math.max(0, window.performance.now() - this.#openedAt);
    const openingGrace = Math.max(0, this.#getExitDuration() - elapsed);
    const delay = Math.max(240, openingGrace + 120);

    this.#closeTimer = window.setTimeout(() => {
      if (this.#pointerInside(this.#activeTrigger) || this.#pointerInside(this.dialog)) return;
      this.close({ restoreFocus: false });
    }, delay);
  }

  #recordPointer(event) {
    if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return;
    this.#pointerX = event.clientX;
    this.#pointerY = event.clientY;
  }

  #pointerInside(element) {
    if (!(element instanceof HTMLElement) || this.#pointerX < 0 || this.#pointerY < 0) return false;
    const rect = element.getBoundingClientRect();
    return (
      this.#pointerX >= rect.left &&
      this.#pointerX <= rect.right &&
      this.#pointerY >= rect.top &&
      this.#pointerY <= rect.bottom
    );
  }

  #clearCloseTimer = () => {
    if (this.#closeTimer) window.clearTimeout(this.#closeTimer);
    this.#closeTimer = 0;
  };

  #clearTimers() {
    this.#clearCloseTimer();
    if (this.#openTimer) window.clearTimeout(this.#openTimer);
    if (this.#hideTimer) window.clearTimeout(this.#hideTimer);
    this.#openTimer = 0;
    this.#hideTimer = 0;
  }

  #setupMobileKineticScroll() {
    if (this.mode !== 'mobile' || !('PointerEvent' in window)) return;
    this.#teardownMobileKineticScroll();

    this.panels.forEach((scroller) => {
      scroller.classList.add('amz-mega__panel-content--kinetic');

      const state = {
        active: false,
        pointerId: null,
        startY: 0,
        lastY: 0,
        lastTime: 0,
        velocity: 0,
        moved: false,
        suppressClickUntil: 0,
        frame: 0,
        frameTime: 0,
      };

      const stopAnimation = () => {
        if (state.frame) window.cancelAnimationFrame(state.frame);
        state.frame = 0;
        state.frameTime = 0;
        state.velocity = 0;
      };

      const setScrollTop = (next) => {
        const maximum = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
        const clamped = Math.min(maximum, Math.max(0, next));
        const previous = scroller.scrollTop;
        scroller.scrollTop = clamped;
        return scroller.scrollTop - previous;
      };

      const allowsInertia = () => {
        const policy = this.layer?.dataset.motionPolicy || 'always';
        if (policy === 'minimal') return false;
        return !(policy === 'respect' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      };

      const animate = (now) => {
        if (!state.frameTime) state.frameTime = now;
        const elapsed = Math.min(32, Math.max(1, now - state.frameTime));
        state.frameTime = now;

        const distance = state.velocity * elapsed;
        const actual = setScrollTop(scroller.scrollTop + distance);
        const friction = Math.pow(0.89, elapsed / 16.667);
        state.velocity *= friction;

        const atBoundary = Math.abs(actual - distance) > 0.5;
        if (atBoundary || Math.abs(state.velocity) < 0.015) {
          stopAnimation();
          return;
        }
        state.frame = window.requestAnimationFrame(animate);
      };

      const finishGesture = (event) => {
        if (!state.active || event.pointerId !== state.pointerId) return;
        state.active = false;
        scroller.classList.remove('is-dragging');
        if (scroller.hasPointerCapture?.(event.pointerId)) scroller.releasePointerCapture(event.pointerId);

        if (state.moved) state.suppressClickUntil = window.performance.now() + 360;
        if (state.moved && allowsInertia() && Math.abs(state.velocity) >= 0.02) {
          state.frameTime = 0;
          state.frame = window.requestAnimationFrame(animate);
        } else {
          stopAnimation();
        }
        state.pointerId = null;
      };

      const onPointerDown = (event) => {
        if (this.desktopMedia.matches || event.pointerType !== 'touch' || !event.isPrimary) return;
        stopAnimation();
        state.active = true;
        state.pointerId = event.pointerId;
        state.startY = event.clientY;
        state.lastY = event.clientY;
        state.lastTime = event.timeStamp || window.performance.now();
        state.velocity = 0;
        state.moved = false;
        scroller.setPointerCapture?.(event.pointerId);
      };

      const onPointerMove = (event) => {
        if (!state.active || event.pointerId !== state.pointerId) return;
        const now = event.timeStamp || window.performance.now();
        const elapsed = Math.max(8, now - state.lastTime);
        const fingerDistance = event.clientY - state.lastY;
        const requested = -fingerDistance * 0.72;
        const actual = setScrollTop(scroller.scrollTop + requested);
        const instantaneousVelocity = actual / elapsed;

        state.velocity = state.velocity * 0.72 + instantaneousVelocity * 0.28;
        state.lastY = event.clientY;
        state.lastTime = now;

        if (Math.abs(event.clientY - state.startY) > 5) {
          state.moved = true;
          scroller.classList.add('is-dragging');
          if (event.cancelable) event.preventDefault();
        }
      };

      const onClick = (event) => {
        if (window.performance.now() >= state.suppressClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      };

      scroller.addEventListener('pointerdown', onPointerDown);
      scroller.addEventListener('pointermove', onPointerMove, { passive: false });
      scroller.addEventListener('pointerup', finishGesture);
      scroller.addEventListener('pointercancel', finishGesture);
      scroller.addEventListener('click', onClick, true);

      this.#scrollStops.push(stopAnimation);
      this.#scrollCleanups.push(() => {
        stopAnimation();
        scroller.classList.remove('amz-mega__panel-content--kinetic', 'is-dragging');
        scroller.removeEventListener('pointerdown', onPointerDown);
        scroller.removeEventListener('pointermove', onPointerMove);
        scroller.removeEventListener('pointerup', finishGesture);
        scroller.removeEventListener('pointercancel', finishGesture);
        scroller.removeEventListener('click', onClick, true);
      });
    });
  }

  #stopMobileKineticScroll() {
    this.#scrollStops.forEach((stop) => stop());
  }

  #teardownMobileKineticScroll() {
    this.#scrollCleanups.forEach((cleanup) => cleanup());
    this.#scrollCleanups = [];
    this.#scrollStops = [];
  }

  #getExitDuration() {
    if (!this.layer) return 600;
    const styles = window.getComputedStyle(this.layer);
    const parseTime = (name, fallback) => {
      const value = Number.parseFloat(styles.getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    };
    return Math.max(parseTime('--amz-speed', 500), parseTime('--amz-overlay-speed', 450)) + 80;
  }

  #setTriggerExpanded(expanded) {
    const value = expanded ? 'true' : 'false';
    this.mobileTrigger?.setAttribute('aria-expanded', value);
    this.#activeTrigger?.setAttribute('aria-expanded', value);
  }

  #releaseScrollLock() {
    if (!document.querySelector(`${SELECTOR}[open]`)) {
      document.documentElement.classList.remove('amz-mega-scroll-lock');
    }
  }

  #trapFocus(event) {
    const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.closest('[hidden]') && element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get(SELECTOR)) {
  customElements.define(SELECTOR, AmzAdvancedMegaDrawer);
}
